import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import './Lore.css';
import { useTranslation } from 'react-i18next';
import { useMusic } from './hooks/useMusic';
import { MAIN_MENU_MUSIC_PATH } from './config/mainMenuMusic';
import { playSoundEffect } from './hooks/useSoundEffects';
import { API_URL } from './config/apiConfig';
import { useContainerScale } from './hooks/useViewportScale';
import { TutorialMessageBox } from './tutorial/index';
import { useTutorialStatus } from './hooks/useTutorialStatus';

function Lore({ user }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // Viewport scaling for responsive design
  const { 
    scale: viewportScale, 
    offsetX: viewportOffsetX, 
    offsetY: viewportOffsetY,
    baseWidth: containerBaseWidth,
    baseHeight: containerBaseHeight,
    isMobile: isContainerMobile
  } = useContainerScale();
  
  useMusic(MAIN_MENU_MUSIC_PATH);
  const [activeSection, setActiveSection] = useState('factions');
  const [loreEntries, setLoreEntries] = useState({
    factions: [],
    mechanics: [],
    orders: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showTutorialLore, setShowTutorialLore] = useState(false);
  const { shouldShowTutorial, markSeenLocally } = useTutorialStatus(user);

  useEffect(() => {
    fetchLoreEntries();
  }, []);

  useEffect(() => {
    if (user && !loading && shouldShowTutorial('lore')) {
      const timer = setTimeout(() => setShowTutorialLore(true), 600);
      return () => clearTimeout(timer);
    }
  }, [user, loading, shouldShowTutorial]);

  const fetchLoreEntries = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/lore-entries`);
      if (!response.ok) {
        throw new Error('Failed to fetch lore entries');
      }
      const data = await response.json();
      setLoreEntries(data);
    } catch (err) {
      console.error('Error fetching lore entries:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getAlphabeticalEntries = () => {
    const currentEntries = loreEntries[activeSection] || [];
    let filteredEntries = currentEntries;
    
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filteredEntries = currentEntries.filter(entry => {
        const title = (entry.title || '').toLowerCase();
        
        // Use word boundary regex to avoid partial matches, only search titles
        const searchRegex = new RegExp(`\\b${searchLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'i');
        
        return searchRegex.test(title);
      });
    }
    
    return filteredEntries.sort((a, b) => {
      const titleA = a.title || '';
      const titleB = b.title || '';
      return titleA.localeCompare(titleB);
    });
  };

  const handleEntryClick = (entry) => {
    setSelectedEntry(entry);
  };

  // Function to parse markdown-style formatting
  const parseText = (text) => {
    if (!text) return '';
    
    // Convert **bold** to <strong>bold</strong>
    let parsed = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Convert *italic* to <em>italic</em>
    parsed = parsed.replace(/\*(.*?)\*/g, '<em>$1</em>');
    
    // Handle both \n and \\n (escaped newlines)
    parsed = parsed.replace(/\\n/g, '<br>');
    parsed = parsed.replace(/\n/g, '<br>');
    
    return parsed;
  };

  const renderLoreEntry = (entry) => (
    <div key={entry.id} className="lore-entry">
      <div className="lore-entry-image-column">
        {entry.image && (
          <img 
            src={entry.image} 
            alt={entry.title} 
            className="lore-entry-image"
          />
        )}
      </div>
      <div className="lore-entry-content-column">
        <h3 className="lore-entry-title">{entry.title}</h3>
        <div 
          className="lore-entry-text"
          dangerouslySetInnerHTML={{ __html: parseText(entry.text) }}
        />
      </div>
    </div>
  );

  const renderSection = () => {
    if (loading) {
      return <div className="loading">{t('lore.loading')}</div>;
    }

    if (error) {
      return <div className="error">{t('lore.error')} {error}</div>;
    }

    const entries = loreEntries[activeSection] || [];
    
    if (entries.length === 0) {
      return <div className="no-entries">{t('lore.noEntries')} {activeSection}.</div>;
    }

    // If an entry is selected, show only that entry
    if (selectedEntry) {
      return (
        <div className="lore-entries">
          {renderLoreEntry(selectedEntry)}
        </div>
      );
    }

    // Show welcome message when no entry is selected
    return (
      <div className="welcome-message">
        <h3>{t('lore.selectEntry')}</h3>
        <p>{t('lore.selectEntryMessage')}</p>
      </div>
    );
  };

  return (
    <div className="lore-viewport-wrapper">
      {/* Full-screen background - outside viewport-container to cover entire screen */}
      <div 
        className="lore-fullscreen-background"
        style={{ backgroundImage: 'url(/images/lore.png)' }}
      />
      
      {/* Viewport container - 1920x1080 space that scales to fit screen */}
      <div 
        className={`lore-viewport-container${isContainerMobile ? ' mobile' : ''}`}
        style={{
          width: `${containerBaseWidth}px`,
          height: `${containerBaseHeight}px`,
          transformOrigin: '0 0',
          transform: `translate(${viewportOffsetX}px, ${viewportOffsetY}px) scale(${viewportScale})`,
        }}
      >
        <div className="lore-container">
      
          <div className="lore-header">
            <div className="lore-header-title-block">
              <h1 className="lore-title">{t('lore.title')}</h1>
            </div>
            <button 
              className="back-button" 
              onClick={() => {
                playSoundEffect('/sounds/click.mp3');
                navigate('/welcome');
              }}
            >
              {t('common.back')}
            </button>
          </div>

      <div className="lore-navigation">
        <button 
          className={`nav-button ${activeSection === 'factions' ? 'active' : ''}`}
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            setActiveSection('factions');
            setSelectedEntry(null);
            setSearchTerm('');
          }}
        >
          {t('lore.factions')}
        </button>
        <button 
          className={`nav-button ${activeSection === 'mechanics' ? 'active' : ''}`}
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            setActiveSection('mechanics');
            setSelectedEntry(null);
            setSearchTerm('');
          }}
        >
          {t('lore.mechanics')}
        </button>
        <button 
          className={`nav-button ${activeSection === 'orders' ? 'active' : ''}`}
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            setActiveSection('orders');
            setSelectedEntry(null);
            setSearchTerm('');
          }}
        >
          {t('lore.orders')}
        </button>
        <button 
          className={`nav-button ${activeSection === 'order-type' ? 'active' : ''}`}
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            setActiveSection('order-type');
            setSelectedEntry(null);
            setSearchTerm('');
          }}
        >
          {t('lore.orderTypes')}
        </button>
      </div>

      <div className="lore-content">
        <div className="lore-sidebar">
          <div className="lore-search-row">
            <input
              type="text"
              className="lore-search-input"
              placeholder={t('lore.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoComplete="off"
            />
          </div>
          <div className="alphabetical-list">
            {loading && (
              <div className="lore-list-placeholder lore-list-placeholder--muted">{t('lore.loading')}</div>
            )}
            {!loading && !error && loreEntries[activeSection]?.length > 0 &&
              getAlphabeticalEntries().map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  className={`alphabetical-entry ${selectedEntry?.id === entry.id ? 'active' : ''}`}
                  onClick={() => {
                    playSoundEffect('/sounds/click.mp3');
                    handleEntryClick(entry);
                  }}
                >
                  {entry.title}
                </button>
              ))}
          </div>
        </div>
        <div className="lore-main-content">
          {renderSection()}
        </div>
      </div>

      {showTutorialLore && createPortal(
        <TutorialMessageBox
          image="/images/tutorial_explain_3.png"
          text={t('lore.tutorialText')}
          onContinue={async () => {
            const token = localStorage.getItem('token');
            if (token) {
              await fetch(`${API_URL}/tutorial/mark-seen`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ messageKey: 'lore' }),
              });
              markSeenLocally('lore');
            }
            setShowTutorialLore(false);
          }}
          voiceKey="lore.tutorialText"
        />,
        document.body
      )}
        </div>
      </div>
    </div>
  );
}

export default Lore;
